import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import uniqBy from 'lodash/uniqBy';
import { useSnackbar } from 'notistack';
import pluralize from 'pluralize';
import { useSelector } from 'react-redux';
import * as RE from '~/reducers/experiences';

import { Alert, Autocomplete, Box, Stack, TextField, debounce } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';

import { Experiences } from '@luxuryescapes/contract-svc-experience';

import { FILTER_RANGE_KEYS, FilterKeys } from '~/consts/experiences';

import * as ES from '~/services/ExperiencesService';
import { UpdateRegionHeroesPayload } from '~/services/ExperiencesService';
import SearchService, { TypeaheadType } from '~/services/SearchService';

import { FieldValueParams } from '../../Experiences';
import { ExperiencesFormFilters, SearchModeOptions } from '../../ExperiencesFormFilters';

import ExperiencesList from './ExperiencesList';
import HeroesList from './HeroesList';

const SIZE_PER_PAGE = 50;

type CityOption = { label: string; placeId: string; region?: UpdateRegionHeroesPayload['region'] };

const RELEVANT_TYPEAHEAD_TYPES: Array<TypeaheadType> = ['city', 'multi_city_vicinity'];

export default function RegionCuration() {
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useSelector((state: App.State) => state.tenant);
  const [experiences, experiencesDispatch] = useReducer(RE.fetchExperiencesReducer, RE.initialExperiences);
  const [fieldSearchValue, setFieldSearchValue] = useState('');
  const cancelFetchRef = useRef<() => void | undefined>(undefined);
  const [searchModeSelected, setSearchModeSelected] = useState(SearchModeOptions.TITLE);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ES.Filters>({
    ...ES.initialFiltersValues,
  });

  const [categories, categoriesDispatch] = useReducer(RE.fetchCategoriesReducer, RE.initialCategories);

  const brand = useSelector((state: App.State) => state.tenant.brand);
  const [selectedExperiences, setSelectedExperiences] = useState<Array<Experiences.Offer>>([]);

  const [selectedCity, setSelectedCity] = useState<CityOption | undefined>();
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | undefined>();
  const [cities, setCities] = useState<Array<CityOption>>([]);
  const [isSearchingCities, setIsSearchingCities] = useState(false);
  const [searchCity, setSearchCity] = useState('');

  const resetFilters = useCallback(
    (hard = false) => {
      setFilters((filter) => {
        const resetedFilters = {
          ...ES.initialFiltersValues,
        };

        if (!hard) {
          Object.values(SearchModeOptions).forEach((key) => (resetedFilters[key] = filter[key] ?? null));
        } else {
          setFieldSearchValue('');
        }

        return resetedFilters;
      });
    },
    [filters],
  );

  const fetchExperiencesWithFilters = useCallback(
    (filters: ES.Filters, page: number) => {
      if (!selectedCity) {
        return;
      }
      experiencesDispatch({ type: 'LOAD' });
      const updatedFilters = { ...filters };
      if ((searchModeSelected !== SearchModeOptions.ID || !updatedFilters.idIn?.length) && coordinates) {
        updatedFilters.coordinates = coordinates;
        updatedFilters.distance = '50KM';
      }
      deboucedFetchExperiences(updatedFilters, page);
    },
    [coordinates, filters, selectedCity, experiencesDispatch, searchModeSelected],
  );

  const refetchExperiences = useCallback(() => {
    onPageChange(1);
    fetchExperiencesWithFilters(filters, 1);
  }, [filters, fetchExperiencesWithFilters]);

  const onChangeSearchValue = (fieldSearchValue: string, searchModeSelected: string) => {
    const newFilters = { ...filters };
    Object.values(SearchModeOptions).forEach((key) => delete newFilters[key]);
    newFilters[searchModeSelected] = fieldSearchValue;
    setFilters(newFilters);
  };

  useEffect(() => {
    if (selectedCity) {
      experiencesDispatch({ type: 'LOAD' });
      SearchService.getPlace(selectedCity.placeId).then((res) => {
        setCoordinates({
          lng: res.result.centroid.coordinates[0],
          lat: res.result.centroid.coordinates[1],
        });
      });
    } else {
      setCoordinates(undefined);
      resetFilters(true);
    }
  }, [selectedCity]);

  useEffect(() => {
    if (coordinates) {
      refetchExperiences();
    }
  }, [coordinates]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (filters) {
      refetchExperiences();
    }
  }, [filters]);

  useEffect(() => {
    fetchExperiencesWithFilters(filters, page);
  }, [page]);

  useEffect(() => {
    if (tenant) {
      fetchCategories();
      refetchExperiences();
    }
  }, [tenant]);

  useEffect(() => {
    onChangeSearchValue(fieldSearchValue, searchModeSelected);
  }, [fieldSearchValue]);

  useEffect(() => {
    if (fieldSearchValue !== '') {
      setFieldSearchValue('');
    }
  }, [searchModeSelected]);

  const setFiltersByKey = useCallback(
    (key: FilterKeys, value: string) => {
      const items = filters[key] ?? [];
      setFilter(key, items.includes(value) ? items.filter((item) => item !== value) : [...items, value]);
    },
    [filters.categories, filters.status, filters.providerIn],
  );

  const handleFieldValue = useCallback(
    (params: FieldValueParams) => {
      const { key, range, fieldValue } = params;

      if (FILTER_RANGE_KEYS.includes(key)) {
        setFilters((filters) => ({
          ...filters,
          [key]: { ...filters[key], [range]: fieldValue },
        }));
      }
    },
    [filters.price, filters.rating],
  );

  const debouncedSearchCities = useMemo(
    () =>
      debounce(async (search: string) => {
        const res = await SearchService.typeahead(search, {
          type: RELEVANT_TYPEAHEAD_TYPES,
          brand: brand.toLowerCase(),
          region: 'AU',
          limit: 200,
          priority: 'current',
        }).finally(() => setIsSearchingCities(false));

        const cities = uniqBy(res.result, 'id').map((result) => ({
          label: `${result.format.mainText}${result.format.secondaryText ? ` (${result.format.secondaryText})` : ''}`,
          placeId: result.id,
        }));
        setCities(cities);
      }, 700),
    [brand, setCities],
  );

  useEffect(() => {
    if (!searchCity) {
      return;
    }
    setIsSearchingCities(true);
    setCities([]);

    debouncedSearchCities(searchCity);

    return () => {
      // Cancel the debounced search
      debouncedSearchCities.clear();
    };
  }, [debouncedSearchCities, searchCity]);

  const fetchCategories = async (): Promise<void> => {
    categoriesDispatch({ type: 'LOAD' });
    try {
      const res = await ES.getCategories(tenant.brand);

      if (res?.errors?.length > 0) {
        categoriesDispatch({
          type: 'ERROR',
          message: "[ERROR] Categories response doesn't exist.",
        });
      }

      categoriesDispatch({
        type: 'FETCH',
        payload: res.result,
      });
    } catch (error: any) {
      console.error('ERROR ON FETCH CATEGORIES: ', error);
      categoriesDispatch({ type: 'ERROR', message: error.message });
    }
  };

  const fetchExperiences = async (filters: ES.Filters, page: number): Promise<void> => {
    experiencesDispatch({ type: 'LOAD' });
    try {
      const offset: number = (page - 1) * SIZE_PER_PAGE;

      const res = await ES.getExperienceOffers({
        curationData: true,
        offset,
        filters,
        limit: SIZE_PER_PAGE,
        brand: tenant.brand,
        sort: 'SCORE',
        sortOrientation: 'DESC',
        getController: (controller) => {
          if (cancelFetchRef.current) {
            cancelFetchRef.current();
          }
          cancelFetchRef.current = controller.cancel;
        },
      });

      if (res?.errors?.length > 0) {
        experiencesDispatch({
          type: 'ERROR',
          message: "[ERROR] Categories response doesn't exist.",
        });
      }

      experiencesDispatch({
        type: 'FETCH',
        total: res?.total,
        payload: res.result,
      });
    } catch (error: any) {
      console.error('ERROR ON FETCH EXPERIENCES: ', error);
      experiencesDispatch({
        type: 'ERROR',
        message: error.message,
      });
    }
  };

  const deboucedFetchExperiences = useMemo(() => debounce(fetchExperiences, 700), [tenant.brand]);

  const fetchPlaceRegion = async (placeId: string): Promise<UpdateRegionHeroesPayload['region'] | undefined> => {
    try {
      const res = await SearchService.getPlaceAncestors(placeId);
      const country = res.result.find((place) => place.type === 'country');
      return country?.countryCode as UpdateRegionHeroesPayload['region'] | undefined;
    } catch (error) {
      console.error('Failed to fetch place region:', error);
      return undefined;
    }
  };

  const handleSearchFieldValue = useCallback((fieldValue: string) => {
    setFieldSearchValue(fieldValue);
  }, []);

  const onPageChange = (newPage: number): void => {
    setPage(newPage);
  };

  const handleSearchModeSelected = useCallback((event: SelectChangeEvent) => {
    setSearchModeSelected(event.target.value as SearchModeOptions);
  }, []);

  const setFilter = useCallback((name: keyof ES.Filters, value: unknown) => {
    setFilters((filters) => ({ ...filters, [name]: value }));
  }, []);

  const handlePage = useCallback((_, page: number) => onPageChange(page), []);

  const addExperience = (experience: Experiences.Offer) => {
    setSelectedExperiences((experiences) => [...experiences, experience]);
  };

  const removeExperience = (experience: Experiences.Offer) =>
    setSelectedExperiences((experiences) => experiences.filter((item) => item.id !== experience.id));

  const onCitySelected = async (value: CityOption | undefined) => {
    if (value) {
      const region = await fetchPlaceRegion(value.placeId);
      setSelectedCity({ ...value, region });
    } else {
      setSelectedCity(undefined);
      enqueueSnackbar('City data not found', { variant: 'error' });
    }
  };

  const experiencesNotFound =
    !isSearchingCities && !experiences.error && !experiences.loading && experiences.items?.length === 0;
  const hasExperiences = experiences.items?.length > 0;
  const countExp = pluralize('experience', experiences?.total || 0, true);
  const experiencesTitle = `${countExp} found by your search criteria`;

  return (
    <Stack direction="column" spacing={2}>
      <Box>
        <Autocomplete
          freeSolo
          openOnFocus
          options={cities}
          loading={isSearchingCities}
          onInputChange={(_, value) => {
            setSearchCity(value);
          }}
          onChange={(_, value: CityOption | undefined) => {
            onCitySelected(value);
          }}
          value={selectedCity}
          renderInput={(params) => <TextField {...params} label="Search for a city" />}
          filterOptions={(x) => x}
        />
      </Box>

      {selectedCity && (
        <>
          <Box mt={4}>
            <ExperiencesFormFilters
              filters={filters}
              categories={categories}
              resetFilters={resetFilters}
              setFiltersByKey={setFiltersByKey}
              fieldSearchValue={fieldSearchValue}
              handleFieldValue={handleFieldValue}
              handleSearchFieldValue={handleSearchFieldValue}
              fetchExperiencesWithFilters={refetchExperiences}
              searchModeSelected={searchModeSelected}
              setSearchModeSelected={handleSearchModeSelected}
              searchModeOptions={[SearchModeOptions.ID, SearchModeOptions.TITLE, SearchModeOptions.VENDOR]}
            />
          </Box>

          {hasExperiences && (
            <Alert severity="success">
              <strong>{experiencesTitle.toUpperCase()}</strong>
            </Alert>
          )}
          {experiences?.error && (
            <Alert severity="error">
              <strong>ERROR ON FETCH EXPERIENCES</strong>
            </Alert>
          )}
          {experiencesNotFound && (
            <Alert severity="warning">
              <strong>NO EXPERIENCES FOUND</strong>
            </Alert>
          )}

          {hasExperiences && searchModeSelected === SearchModeOptions.ID && !!fieldSearchValue && (
            <Alert severity="warning">
              <strong>CAUTION: filtering by ID ignores the distance from the selected region/city</strong>
            </Alert>
          )}

          <Box mt={2} display="grid" gap={2} gridTemplateColumns={{ sm: '1fr', md: '1fr 1fr' }}>
            <ExperiencesList
              experiences={experiences}
              handlePage={handlePage}
              currentPage={page}
              sizePerPage={SIZE_PER_PAGE}
              experiencesTotal={experiences?.total || 0}
              selectedExperiences={selectedExperiences}
              onAddExperience={addExperience}
              onRemoveExperience={removeExperience}
            />
            <HeroesList
              selectedExperiences={selectedExperiences}
              onRemoveExperience={removeExperience}
              onLoadSelectedExperiences={setSelectedExperiences}
              brand={tenant.brand as UpdateRegionHeroesPayload['brand']}
              region={selectedCity.region}
              placeId={selectedCity.placeId}
            />
          </Box>
        </>
      )}
    </Stack>
  );
}
